<template>
    <v-card v-bind="$attrs" class="layout-container">
        <v-toolbar dense class="elevation-0 layout-header" color="primary white--text">
            <ActionBtn color="primary white--text" depressed @click="newDirBtn">
                <span><KpIcon disabled-color="grey" color="white" icon-cls="new" /> {{ $t('neues_verz') }} </span>
            </ActionBtn>
            <IconBtn
                icon-cls="pencil"
                icon-color="white"
                icon-disabled-color="white"
                :disabled="!activeAlbum || !activeAlbum.id"
                :tooltip="String($t('rename_dir'))"
                @click="renameDirBtn"
            />
            <IconBtn
                icon-cls="delete"
                icon-color="white"
                icon-disabled-color="white"
                :disabled="!activeAlbum || !activeAlbum.id"
                :tooltip="String($t('verz_loeschen'))"
                @click="delDirBtn"
            />
            <span>|</span>
            <ActionBtn ref="uploadBtn" color="primary white--text" depressed :disabled="!activeAlbum">
                <span><KpIcon icon-cls="upload" color="white" disabled-color="grey" /> {{ $t('upload') }}</span>
            </ActionBtn>
            <span>|</span>

            <ActionBtn v-if="!hideMultiselect" :disabled="checkedItems.length <= 0" text @click="deleteCheckedItems">
                <KpIcon :tooltip="$t('delete_checked')" icon-cls="trash" color="white" disabled-color="white" />
            </ActionBtn>
            <ActionBtn v-if="!hideMultiselect" :disabled="checkedItems.length <= 0" text @click="unCheckItems">
                <KpIcon
                    :tooltip="$t('uncheck_items')"
                    icon-cls="checkboxunchecked"
                    color="white"
                    disabled-color="white"
                />
            </ActionBtn>

            <v-spacer />

            <!-- Sort-Selector -->
            <v-menu offset-y>
                <template #activator="{ on, attrs }">
                    <v-btn color="white tgk-paragraph-text" v-bind="attrs" v-on="on">
                        <KpIcon icon-cls="sortdown" color="grey" /> {{ $t('sort_by') }}
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item-group v-model="imageSort">
                        <v-list-item dense value="title">
                            <v-list-item-icon>
                                <KpIcon v-if="imageSort === 'title'" icon-cls="tick" color="grey" />
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('titel') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item dense value="orig_filename">
                            <v-list-item-icon>
                                <KpIcon v-if="imageSort === 'orig_filename'" icon-cls="tick" color="grey" />
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('dateiname') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item dense value="created">
                            <v-list-item-icon>
                                <KpIcon v-if="imageSort === 'created'" icon-cls="tick" color="grey" />
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('created') }}</v-list-item-title>
                        </v-list-item>
                    </v-list-item-group>
                    <v-divider />
                    <v-list-item-group v-model="imageSortDir">
                        <v-list-item dense value="asc">
                            <v-list-item-icon>
                                <KpIcon v-if="imageSortDir === 'asc'" icon-cls="tick" color="grey" />
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('asc') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item dense value="desc">
                            <v-list-item-icon>
                                <KpIcon v-if="imageSortDir === 'desc'" icon-cls="tick" color="grey" />
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('desc') }}</v-list-item-title>
                        </v-list-item>
                    </v-list-item-group>
                </v-list>
            </v-menu>

            <QueryField
                v-if="userIsStaff()"
                v-model="searchQuery"
                class="ml-1"
                @keydown.enter.stop="search"
                @click:clear="clearSearch"
            />
        </v-toolbar>

        <!-- folder tree -->
        <div class="layout-dirtree">
            <div v-if="albumsLoading" class="ma-5 text-center">
                <KpIcon icon-cls="load ic-spin" color="grey" />
            </div>
            <v-treeview
                v-if="!albumsLoading"
                class="flex-grow-1 overflow-auto"
                activatable
                dense
                :items="albumItems"
                :open="openAlbums"
                :active="activeAlbums"
                active-class="dirtree-node-active"
                item-text="title"
                return-object
                @update:active="onActivateAlbum"
            >
                <template #prepend="_">
                    <v-icon>
                        {{ 'ic ic-16 ic-folder ic-col-grey' }}
                    </v-icon>
                </template>
                <template #label="itemProps">
                    <span
                        :class="{
                            'tgk-paragraph-text': true,
                            'font-italic': activeAlbums.length > 0 && activeAlbums[0] === itemProps.item,
                            'py-4': true,
                            'd-block': true,
                        }"
                        @dragenter="onDragOverAlbum"
                        @dragover="onDragOverAlbum"
                        @dragleave="onDragLeaveAlbum"
                        @drop="
                            (ev) => {
                                onDropOnAlbum(itemProps, ev);
                            }
                        "
                    >
                        {{ itemProps.item.title }}
                    </span>
                </template>
            </v-treeview>
            <ActionBtn v-if="userIsStaff()" class="ma-1" @click="showAdditionalEoDlg = true">
                <KpIcon icon-cls="mediamanager" color="white" disabled-color="white" />
                {{ $t('show_additional_eo') }}
            </ActionBtn>
        </div>

        <!-- Content Toolbar -->
        <div class="layout-content-toolbar">
            <div v-if="mode === 'browse'">
                <album-breadcrumbs :selected-album="activeAlbum" @click="onActivateAlbum([$event])" />
            </div>
            <div v-if="mode === 'search'">
                <span class="tgk-paragraph-text">
                    {{ $t('search') }}: <strong>'{{ searchQuery }}'</strong>
                </span>
            </div>
        </div>

        <!-- Content (Main view) -->
        <div
            ref="contentPanel"
            :class="{ 'layout-content': true, 'drag-over': dragIndicatorUpload }"
            @dragenter="onDragOverUpload"
            @dragover="onDragOverUpload"
            @dragleave="onDragLeaveUpload"
        >
            <ContentView
                :loading="imageCollectionLoading"
                :items="imageCollection ? imageCollection.models : []"
                :initial-selected="selectedMedia"
                :sort-by="imageSort"
                :sort-order="imageSortDir"
                :initial-checked="checkedItems"
                :hide-multiselect="hideMultiselect"
                :page="imageCollectionPage"
                :page-size="imageCollectionPageSize"
                :total-items="imageCollection?.get('remoteTotal') || 0"
                @page="imageCollectionPage = $event"
                @change="onImageSelectionChange"
                @delete="deleteImage"
                @duplicate="duplicateImage"
                @checked-items="checkedItems = $event"
            />
        </div>

        <!-- Detail form for a single image -->
        <div :class="{ 'layout-detail': true, hide: !showDetails }">
            <DetailView
                v-if="selectedMedia"
                :selected-item="selectedMedia"
                @image-updated="selectedMediaUpdated($event)"
            />
        </div>

        <v-dialog v-model="newDirDlg" max-width="500px">
            <v-card>
                <v-card-title>{{ $t('neues_verz') }} in: {{ activeAlbum ? activeAlbum.title : '-' }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="newDirName" autofocus :label="$t('neues_verz')" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="newDirDlg = false">{{ $t('cancel') }}</v-btn>
                    <v-btn color="action" text :disabled="!newDirName" @click="saveNewDir">{{ $t('save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="renameDirDlg" max-width="500px">
            <v-card>
                <v-card-title>{{ $t('rename_dir') }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="newDirName" autofocus :label="$t('titel')" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="newDirDlg = false">{{ $t('cancel') }}</v-btn>
                    <v-btn color="action" text :disabled="!newDirName" @click="renameDir">{{ $t('save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <confirm-dialog
            v-model="delDirDlg"
            :title="$t('verz_loeschen')"
            :message="$t('verz_loeschen_info')"
            :context="activeAlbum ? activeAlbum.title : ''"
            @confirm="onDelDir"
        />

        <v-dialog v-model="uploadDlg" max-width="500px" persistent>
            <v-card>
                <v-card-title>{{ $t('upload') }}</v-card-title>
                <v-card-text>
                    <div v-for="(f, i) in flowFiles" :key="f.uniqueIdentifier" class="mb-1">
                        <v-progress-linear striped height="20" :value="flowFileProgress[i] * 100">
                            {{ f.name }}
                        </v-progress-linear>
                    </div>
                    <v-banner v-if="uploadErrors.length > 0" color="error" class="ma-2">
                        <div v-for="(err, i) in uploadErrors" :key="i">
                            {{ err }}
                        </div>
                    </v-banner>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="onUploadCancel">{{ $t('cancel') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <DependencyDialog v-if="showDependencyDialog" v-model="showDependencyDialog" :dependencies="dependencies" />
        <SelectAdditionalEoDlg
            v-model="showAdditionalEoDlg"
            :persistent="false"
            @selected="onAdditionalEventOrganizerSelect"
        ></SelectAdditionalEoDlg>
    </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { errToStr } from '@/helpers/tools';
import { Image, ImageCollection } from '@/models/Image';
import { Album, AlbumCollection } from '@/models/Album';
import ContentView from './ContentView.vue';
import DetailView from './DetailView.vue';
import AlbumBreadcrumbs from './PathCrumb.vue';
import Flow from '@flowjs/flow.js';
import { apiUrl } from '@/helpers/globals';
import { instance as api } from '@/helpers/api';
import { getCsrfToken, userIsStaff } from '@/helpers/auth';
import sessionStorageMixin from '@/mixins/session-storage';
import DependencyDialog from '@/components/DependencyDialog.vue';
import ActionBtn from '../buttons/ActionBtn.vue';
import SelectAdditionalEoDlg from './SelectAdditionalEoDlg.vue';

/**
 * The media_manager component allows uploading and storing media - images, at the moment.
 *
 * It is meant to work as standalone component, as well as embedded in bigger views.
 * It uses FlowJS for uploading content parallel in junks.
 *
 * Albums are loaded as a full dir-tree, no need to lazy load child albums:
 * it seems that the tree component is not that happy with lazy-loading
 * childs - don't even try it, especially if you want to keep track of
 * WHAT is already opened...
 */
export default {
    name: 'MediaManagerMainView',
    components: {
        ContentView,
        DetailView,
        AlbumBreadcrumbs,
        DependencyDialog,
        ActionBtn,
        SelectAdditionalEoDlg,
    },
    mixins: [sessionStorageMixin],
    props: {
        // needed for sessionSet/Get to identify the component uniquely. Provide something like "user-list".
        sessionKey: {
            type: String,
            required: true,
        },

        initialSelected: {
            type: Image,
            default: null,
        },
        /** if true, the image's multiselect functions are not shown */
        hideMultiselect: {
            type: Boolean,
            default: false,
        },
        organizerId: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            // mode: browse or search, changes UI behaviour
            mode: 'browse',

            albumsLoading: true,
            activeAlbums: [],
            openAlbums: [],
            showDetails: this.initialSelected ? true : false,
            imageCollectionLoading: false,
            imageCollection: null,
            imageCollectionPage: 1,
            imageCollectionPageSize: 50,
            selectedMedia: this.initialSelected,
            additionalOrganizerIds: [],
            showAdditionalEoDlg: false,

            newDirDlg: false,
            renameDirDlg: false,
            newDirName: '',

            delDirDlg: false,
            delDirAlbum: null,

            dragIndicatorUpload: false,

            // store the flowjs file infos:
            flowFiles: [],
            // separate progess for uploading: each entry corresponds to the same index in flowFiles:
            flowFileProgress: [],
            uploadDlg: false,
            uploadErrors: [],

            imageSort: this.getSessionValue('imageSort', 'created'),
            imageSortDir: this.getSessionValue('imageSortDir', 'desc'),

            searchQuery: '',

            checkedItems: [],
            dependencies: [],
            albumItems: [],
            showDependencyDialog: false,
        };
    },

    computed: {
        ...mapState('album', {
            loaded: 'loaded',
            userRootAlbum: 'userRootAlbum',
        }),
        activeAlbum() {
            if (this.activeAlbums && this.activeAlbums.length) {
                return this.activeAlbums[0];
            } else {
                return null;
            }
        },
    },

    watch: {
        activeAlbum(act, old) {
            if (act && act !== old) {
                this.switchMode('browse', true);
            }
            this.setSessionValue('activeAlbumId', act ? act.id : null);
        },

        imageSort(act, old) {
            if (act !== old) {
                this.setSessionValue('imageSort', act);
                this.reloadAlbumContent();
            }
        },

        imageSortDir(act, old) {
            if (act !== old) {
                this.setSessionValue('imageSortDir', act);
                this.reloadAlbumContent();
            }
        },
        imageCollectionPage(act, old) {
            if (act !== old) {
                this.reloadAlbumContent();
            }
        },
    },

    created() {
        this.userIsStaff = userIsStaff;
    },

    mounted() {
        this.loadAlbums({ force: false });

        // flowjs is initialized on mounting:
        this.initializeFlowUpload();
    },

    methods: {
        ...mapActions(['runAsync']),
        async loadAlbums({ force }) {
            this.albumsLoading = true;
            await this.$store.dispatch('album/loadUserRootAlbum', { force });
            this.albumsLoading = false;

            // select initial active album, if stored in session:
            let initialAlbumId = this.getSessionValue('activeAlbumId');
            let album = this.userRootAlbum;
            if (initialAlbumId !== null) {
                album = this.userRootAlbum.findAlbumBy('id', initialAlbumId);
            }
            this.albumItems = [this.userRootAlbum];

            // if set, load the event organizer's root album, too,
            // and set it as opened album, in addition to manually added album IDs:
            const organizers = [...this.additionalOrganizerIds, this.organizerId].filter(Boolean);
            if (organizers.length) {
                const eoAlbums = new AlbumCollection();
                await eoAlbums.query({
                    event_organizer_id: JSON.stringify(organizers),
                });
                if (eoAlbums.length) {
                    this.albumItems = [...this.albumItems, ...eoAlbums.models];
                    album = eoAlbums.models[0];
                }
            }

            // Activate either root album or the one stored in the session:
            this.onActivateAlbum([album]);

            // open all (parent) albums of the actual one in the hierarchy:
            let opened = [];
            while (album) {
                opened.push(album);
                album = album.parent;
            }
            this.openAlbums = opened;
        },
        reloadAlbums() {
            this.loadAlbums({ force: true });
        },
        openAlbum(album) {
            if (album && this.openAlbums.indexOf(album) < 0) {
                this.openAlbums = [...this.openAlbums, album];
            }
        },
        onActivateAlbum(albums) {
            if (albums.length && this.activeAlbums[0] !== albums[0]) {
                this.activeAlbums = [albums[0]];
                this.openAlbum(albums[0]);
            }
        },

        async loadAlbumContent(album) {
            let queryParam = {
                limit: this.imageCollectionPageSize,
                start: this.imageCollectionPageSize * (this.imageCollectionPage - 1),
            };
            if (this.mode === 'browse') {
                if (!album) {
                    return;
                }
                queryParam.album_id = album.id;
            } else if (this.mode === 'search') {
                let q = (this.searchQuery || '').trim();
                if (!q) {
                    return;
                }
                queryParam.quicksearch = q;
            } else {
                return;
            }
            try {
                let content = new ImageCollection();
                this.imageCollection = content;
                this.imageCollectionLoading = true;
                await this.runAsync(() =>
                    content
                        .setRemoteSort([{ property: this.imageSort, direction: this.imageSortDir }])
                        .query(queryParam),
                );
                this.checkedItems = [];
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.imageCollectionLoading = false;
            }
        },

        onAdditionalEventOrganizerSelect(eo) {
            if (eo) {
                this.additionalOrganizerIds = [...this.additionalOrganizerIds, eo.id];
                this.reloadAlbums();
            }
        },

        onImageSelectionChange(img) {
            if (img) {
                this.showDetails = true;
                this.selectedMedia = img;
            } else {
                this.showDetails = false;
                this.selectedMedia = null;
            }
            this.$emit('select-image', img);
        },

        newDirBtn() {
            this.newDirName = '';
            this.newDirDlg = true;
        },
        renameDirBtn() {
            if (this.activeAlbum) {
                this.newDirName = this.activeAlbum.title;
                this.renameDirDlg = true;
            }
        },
        async saveNewDir() {
            let dirName = (this.newDirName || '').trim();
            this.newDirDlg = false;
            let parent = this.activeAlbum;
            if (parent && dirName) {
                try {
                    let dir = new Album();
                    dir.set({ title: dirName, parent_album_id: parent.id || null });
                    await this.runAsync(() => dir.save());
                    parent.addChild(dir);
                    this.openAlbum(parent);
                    this.openAlbum(dir);
                    this.onActivateAlbum([dir]);
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                }
            }
        },
        async renameDir() {
            let dirName = (this.newDirName || '').trim();
            this.renameDirDlg = false;
            let dir = this.activeAlbum;
            if (dir && dirName) {
                try {
                    dir.set({ title: dirName });
                    await this.runAsync(() => dir.save());
                } catch (e) {
                    console.log(e.stack);
                    this.$toast({ message: errToStr(e), color: 'error' });
                }
            }
        },
        delDirBtn() {
            this.delDirAlbum = this.activeAlbum;
            if (this.delDirAlbum) {
                this.delDirDlg = true;
            }
        },
        async onDelDir() {
            if (this.delDirAlbum) {
                try {
                    await this.runAsync(() => this.delDirAlbum.delete());
                    if (this.delDirAlbum.parent) {
                        this.delDirAlbum.parent.removeChild(this.delDirAlbum);
                    }
                    this.activeAlbums = [];
                    this.reloadAlbums();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                }
            }
        },

        async switchMode(mode, forceReload = false) {
            if (mode && (mode !== this.mode || forceReload)) {
                this.imageCollectionPage = 1;
                switch (mode) {
                    case 'search':
                        this.mode = 'search';
                        break;
                    case 'browse':
                        this.searchQuery = '';
                        this.mode = 'browse';
                        break;
                }
                await this.reloadAlbumContent();
            }
        },

        async reloadAlbumContent() {
            switch (this.mode) {
                case 'search':
                    await this.loadAlbumContent(null);
                    break;
                case 'browse':
                default:
                    await this.loadAlbumContent(this.activeAlbum);
                    break;
            }
        },

        search() {
            this.switchMode('search', true);
        },

        clearSearch() {
            // we switch back to the 'browse' mode
            this.switchMode('browse');
        },

        async deleteImage(imageRec) {
            if (imageRec) {
                this.imageCollectionLoading = true;
                try {
                    await imageRec.delete();
                    await this.reloadAlbumContent();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.imageCollectionLoading = false;
                }
            }
        },

        async duplicateImage(imageRec) {
            if (imageRec) {
                this.imageCollectionLoading = true;
                try {
                    await api().post('/image/duplicate', {
                        id: imageRec.id,
                    });
                    await this.reloadAlbumContent();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.imageCollectionLoading = false;
                }
            }
        },

        /**
         * FlowJS needs to be initialized on component mount. It attaches itself to
         * the DOM and adds the needed event listeners.
         */
        initializeFlowUpload() {
            this.flowObj = new Flow({
                target: apiUrl('/image/flowUpload'),
                chunkSize: 1024 * 500,
                fileParameterName: 'file',
                uploadMethod: 'POST',
                testChunks: false,
                simultaneousUploads: 3,
                headers: {
                    'x-csrf-token': getCsrfToken(),
                },
                // query is called for each upload file. It attaches
                // the actual selected album id as query param:
                query: () => {
                    return {
                        album_id: this.activeAlbum ? this.activeAlbum.id : null,
                    };
                },
            });

            // attach a button and a drop target for adding files:
            this.flowObj.assignBrowse(this.$refs.uploadBtn.$el, false, false, { accept: 'image/*' });
            this.flowObj.assignDrop(this.$refs.contentPanel);

            // After files are added, we initialize and start the upload process directly:
            this.flowObj.on('filesSubmitted', (files) => {
                this.flowFiles = [...files];
                this.flowFileProgress = this.flowFiles.map(() => 0);
                this.uploadErrors = [];
                this.uploadDlg = true;
                this.dragIndicatorUpload = false;
                this.flowObj.upload();
            });

            // on progress, we need to update the separate progess array:
            this.flowObj.on('fileProgress', (file) => {
                let idx = this.flowFiles.indexOf(file);
                if (idx >= 0) {
                    // setting a reactive array by index (this.flowFileProgress[idx]) does not work,
                    // the reactive system does not detect it. So we use the VMs $set method here:
                    this.$set(this.flowFileProgress, idx, file.progress());
                }
            });

            // when the upload(s) is complete, we reset flowjs and update the UI:
            this.flowObj.on('complete', () => {
                this.flowObj.cancel();
                this.flowFiles = [];
                this.flowFileProgress = [];
                this.dragIndicatorUpload = false;
                if (this.uploadErrors.length === 0) {
                    this.uploadDlg = false;
                }
                this.switchMode('browse', true);
            });

            // finally, if errors occur, we collect them and show them in the upload dialog:
            this.flowObj.on('error', (msg, file, chunk) => {
                this.uploadErrors.push(`${file.name}: ${msg}`);
                console.error(msg, file, chunk);
            });
        },

        // upload is canceled: reset flowjs
        onUploadCancel() {
            this.flowObj.cancel();
            this.uploadDlg = false;
            this.dragIndicatorUpload = false;
            this.flowFiles = [];
            this.flowFilesProgress = [];
        },

        processDragItems(type, ev, fn) {
            if (!ev.dataTransfer) {
                return;
            }
            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                for (const item of ev.dataTransfer.items) {
                    // If dropped items aren't files, reject them
                    if (!type || item.kind === type) {
                        fn(item, ev);
                    }
                }
            } else if (!type || type === 'file') {
                // Use DataTransfer interface to access the file(s)
                for (const file of ev.dataTransfer.files) {
                    fn(file, ev);
                }
            }
        },

        onDragOverUpload(ev) {
            this.processDragItems('file', ev, (item, ev) => {
                ev.preventDefault();
                this.dragIndicatorUpload = true;
            });
        },

        onDragLeaveUpload(ev) {
            ev.preventDefault();
            this.dragIndicatorUpload = false;
        },

        onDragOverAlbum(ev) {
            this.processDragItems('string', ev, (item, ev) => {
                ev.preventDefault();
                if (ev.dataTransfer !== null) {
                    ev.dataTransfer.dropEffect = 'move';
                }
                ev.target.classList.add('drag-over');
            });
        },

        onDragLeaveAlbum(ev) {
            ev.preventDefault();
            ev.target.classList.remove('drag-over');
        },

        async onDropOnAlbum(itemProps, ev) {
            ev.preventDefault();
            ev.target.classList.remove('drag-over');
            // Get the id of the image
            const imageId = ev.dataTransfer?.getData('text/plain');
            const albumId = itemProps.item.getId();
            await this.moveImageToAlbum(imageId, albumId);
        },

        async moveImageToAlbum(imageId, albumId) {
            const collection = new ImageCollection();
            await this.runAsync(async () => await collection.query({ id: imageId }));
            const image = collection.first();
            image.album_id = albumId;
            await image.save();
            await this.reloadAlbumContent();
        },

        async deleteCheckedItems() {
            if (this.checkedItems.length <= 0) {
                return;
            }
            try {
                const collection = new ImageCollection();
                this.dependencies = await this.runAsync(
                    async () => await collection.listDependencies(this.checkedItems),
                );
                if (this.dependencies.length > 0) {
                    this.showDependencyDialog = true;
                } else {
                    this.imageCollectionLoading = true;
                    await this.runAsync(async () => await collection.deleteMultiple(this.checkedItems));
                    this.checkedItems = [];
                    await this.reloadAlbumContent();
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.imageCollectionLoading = false;
            }
        },

        async unCheckItems() {
            this.checkedItems = [];
        },

        selectedMediaUpdated(imageRec) {
            if (this.selectedMedia) {
                this.selectedMedia = imageRec;
            }
            // Replace the changed image in the displayed collection, too,
            // to trigger a re-render of that image
            this.imageCollection.each((image, idx) => {
                if (image.id === imageRec.id) {
                    const models = [...this.imageCollection.models];
                    models[idx] = imageRec;
                    this.imageCollection.replace(models);
                    return false;
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.layout-container {
    height: 100%;

    display: grid;
    grid-template-rows: auto auto 1fr;
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
        'header header header'
        'dirtree content-toolbar detail'
        'dirtree content detail';

    .layout-header {
        grid-area: header;
    }

    .layout-dirtree {
        grid-area: dirtree;
        overflow: hidden;
        height: 100%;
        width: 280px;
        border-right: 1px solid var(--gray-border-color);
        display: flex;
        flex-direction: column;

        ::v-deep {
            .dirtree-node-active {
                background-color: var(--panel-bg-color);
            }
        }

        ::v-deep {
            .v-treeview-node__root {
                /** this is the old-school-way: (ff) */
                .drag-over {
                    background-color: var(--v-primary-base);
                    opacity: 0.3;
                }
                /** this is the modern way: */
                &:has(.drag-over) {
                    background-color: var(--v-primary-base);
                    opacity: 0.3;
                }
            }
        }
    }

    .layout-content-toolbar {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0 5px;
        height: 50px;
        grid-area: content-toolbar;
        border-bottom: 1px solid var(--gray-border-color);
    }

    .layout-content {
        grid-area: content;
        overflow: auto;
        padding: 10px;

        &.drag-over {
            background-color: var(--light-blue-color);
            opacity: 0.3;
        }
    }

    .layout-detail {
        grid-area: detail;
        overflow: auto;
        width: 450px;
        border-left: 1px solid var(--gray-border-color);
        width: 550px;

        &.hide {
            width: 0px;
        }
    }
}
</style>
